/**
 * Axios API client: attaches the access token + active organization, refreshes tokens once on 401,
 * and normalises backend errors to { code, message, details }.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL?.trim().replace(/\/+$/, "") || "";
export const API_BASE = `${BACKEND_URL}/api`;

const STORAGE = {
  access: "og_access_token",
  refresh: "og_refresh_token",
  org: "og_org_id",
};

export const tokenStore = {
  get access() {
    return localStorage.getItem(STORAGE.access);
  },
  get refresh() {
    return localStorage.getItem(STORAGE.refresh);
  },
  get org() {
    return localStorage.getItem(STORAGE.org);
  },
  set(tokens) {
    if (tokens?.access_token) localStorage.setItem(STORAGE.access, tokens.access_token);
    if (tokens?.refresh_token) localStorage.setItem(STORAGE.refresh, tokens.refresh_token);
  },
  setOrg(orgId) {
    if (orgId) localStorage.setItem(STORAGE.org, orgId);
    else localStorage.removeItem(STORAGE.org);
  },
  clear() {
    Object.values(STORAGE).forEach((k) => localStorage.removeItem(k));
  },
};

export const api = axios.create({ baseURL: API_BASE, timeout: 180000 });

api.interceptors.request.use((config) => {
  const token = tokenStore.access;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const org = tokenStore.org;
  if (org && !config.headers["X-Organization-Id"]) config.headers["X-Organization-Id"] = org;
  return config;
});

let refreshing = null;

async function refreshTokens() {
  if (!refreshing) {
    refreshing = axios
      .post(`${API_BASE}/auth/refresh`, { refresh_token: tokenStore.refresh })
      .then((res) => {
        tokenStore.set(res.data);
        return res.data;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const code = error.response?.data?.error?.code;
    if (status === 401 && !original._retry && tokenStore.refresh && code !== "invalid_credentials" && code !== "refresh_invalid") {
      original._retry = true;
      try {
        await refreshTokens();
        original.headers.Authorization = `Bearer ${tokenStore.access}`;
        return api(original);
      } catch (e) {
        tokenStore.clear();
        window.dispatchEvent(new CustomEvent("og:logout"));
      }
    }
    return Promise.reject(normalizeError(error));
  },
);

export function normalizeError(error) {
  const data = error.response?.data;
  const err = new Error(data?.error?.message || error.message || "Request failed");
  err.code = data?.error?.code || (error.code === "ECONNABORTED" ? "timeout" : "network_error");
  err.status = error.response?.status;
  err.details = data?.error?.details;
  return err;
}

export const get = (url, config) => api.get(url, config).then((r) => r.data);
export const post = (url, body, config) => api.post(url, body, config).then((r) => r.data);
export const patch = (url, body, config) => api.patch(url, body, config).then((r) => r.data);
export const put = (url, body, config) => api.put(url, body, config).then((r) => r.data);
export const del = (url, config) => api.delete(url, config).then((r) => r.data);
